import { type ApartmentBatchResponse, isAvailableColumn } from '@sivan/shared';
import { type ReviewPhase, SheetReview } from './SheetReview';
import { type SheetRow, changedFields, displayValue, isCleared } from './apartment-draft';
import { apartmentColumnLabel, apartmentErrorText } from './labels';

export type { ReviewPhase } from './SheetReview';

interface ApartmentReviewProps {
  readonly open: boolean;
  readonly phase: ReviewPhase;
  readonly result: ApartmentBatchResponse | null;
  readonly rows: readonly SheetRow[];
  readonly confirmed: ReadonlySet<string>;
  readonly onToggleConfirm: (allocationId: string, checked: boolean) => void;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
  readonly onKeepMine: (key: string) => void;
  readonly onDiscard: (key: string) => void;
  readonly onFocusCell: (key: string, field: string) => void;
}

const title = (row: SheetRow, rowNumber: number) =>
  `שורה ${String(rowNumber)} · בניין ${displayValue(row, 'building') || '—'} · דירה ${displayValue(row, 'apartmentNumber') || '—'}`;

/** Apartment field changes as the user entered them (the server re-validates everything). */
function fieldChanges(row: SheetRow): string[] {
  return changedFields(row)
    .filter((f) => !f.startsWith('parking') && !f.startsWith('storage'))
    .map((field) => {
      const label = apartmentColumnLabel(field);
      const before = row.base?.[field] ?? '';
      if (isCleared(row, field)) return `${label}: הערך "${before}" יימחק`;
      const after = displayValue(row, field);
      return row.base === null || before === ''
        ? `${label}: ${after}`
        : `${label}: "${before}" ← "${after}"`;
    });
}

/** The unified review of the Apartments sheet: apartments with every allocation they change. */
export function ApartmentReview({ rows, ...props }: ApartmentReviewProps) {
  const byKey = new Map(rows.map((row, index) => [row.key, { row, number: index + 1 }]));
  return (
    <SheetReview
      {...props}
      perspective="apartment"
      labels={{
        create: ['דירה חדשה אחת', 'דירות חדשות'],
        update: ['דירה מתעדכנת', 'דירות מתעדכנות'],
      }}
      errorText={apartmentErrorText}
      isCell={(key) => isAvailableColumn(key, { contact: true, note: true })}
      describe={(key) => {
        const entry = byKey.get(key);
        return entry === undefined
          ? undefined
          : { title: title(entry.row, entry.number), changes: fieldChanges(entry.row) };
      }}
    />
  );
}
